import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { API_BASE } from "@/lib/dominio";
import { useExpediente } from "@/context/ExpedienteContext";
import { Spinner } from "@/components/common";
import { ExpEstadoBadge } from "@/components/portalCommon";
import { FileSignature, FileText, Eye, PenLine, CheckCircle2, X, AlertTriangle } from "lucide-react";
import DocViewer from "@/components/DocViewer";
import FirmaManuscritaModal from "@/components/FirmaManuscritaModal";
import { RogeFelicitacionCard, RogeFelicitacionModal } from "@/components/RogeFelicitacion";

const vigentes = (c) => (c?.documentos || []).filter((d) => d.estado !== "reemplazado");
const porFirmar = (c) => vigentes(c).filter((d) => d.requiere_firma && !d.es_prueba && d.estado !== "firmado");
const todoFirmado = (c) => porFirmar(c).length === 0 && vigentes(c).some((d) => d.requiere_firma && d.estado === "firmado");

const API = API_BASE;

function AccionDoc({ d, onFirmar }) {
  if (d.estado === "firmado") return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${d.pendiente_gerencia ? "border-orange-200 bg-orange-50 text-orange-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid={`portal-firmado-${d.template_key}`}><CheckCircle2 className="h-3.5 w-3.5" /> {d.pendiente_gerencia ? "Firmado por ti · pendiente Gerencia" : "Firmado"}</span>;
  if (d.es_prueba) return <span data-testid={`portal-prueba-${d.template_key}`} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700" title="Las plantillas estaban en borrador al emitir. HUMAN debe aprobarlas y reemitir la versión final.">Documento de prueba — no firmable</span>;
  if (!d.requiere_firma) return <span className="text-xs text-slate-400">Sin firma</span>;
  if (!d.firmable) return <span data-testid={`portal-reemision-${d.template_key}`} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">HUMAN debe emitir una nueva versión</span>;
  return <button onClick={() => onFirmar(d)} data-testid={`portal-firmar-${d.template_key}`} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800"><PenLine className="h-3.5 w-3.5" /> Revisar y firmar</button>;
}

function DocFila({ d, onVer, onFirmar }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3" data-testid={`portal-doc-${d.template_key}`}>
      <FileText className="h-4 w-4 text-vino-700" />
      <div className="min-w-0 flex-1 basis-[70%] sm:basis-0">
        <p className="text-sm font-medium text-slate-800 sm:truncate">{d.nombre} <span className="text-xs font-normal text-slate-400">· v{d.version || 1}</span></p>
        {d.firma && <p className="break-words text-[11px] text-emerald-600">Firmado por ti · {new Date(d.firma.fecha).toLocaleString("es-CO")}{d.firma.codigo ? ` · ${d.firma.codigo}` : ""}</p>}
      </div>
      <div className="flex w-full flex-wrap items-center gap-2 pl-7 sm:w-auto sm:pl-0">
      <button onClick={() => onVer(d)} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-vino-700 hover:bg-vino-50" data-testid={`portal-preview-${d.template_key}`}><Eye className="h-3.5 w-3.5" /> Ver documento</button>
      {onFirmar ? <AccionDoc d={d} onFirmar={onFirmar} /> : <span className="text-xs text-slate-400">Versión anterior</span>}
      </div>
    </div>
  );
}

export default function Contratacion() {
  const { exp } = useExpediente();
  const [cont, setCont] = useState(undefined);
  const [firmar, setFirmar] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [revisar, setRevisar] = useState(null);
  const [hashVisto, setHashVisto] = useState("");
  const [leido, setLeido] = useState(false);
  const [celebrar, setCelebrar] = useState(false);
  const token = localStorage.getItem("roge_token");
  const load = () => api.get("/portal/contratacion").then((r) => { setCont(r.data.contratacion); return r.data.contratacion; }).catch(() => { setCont(null); return null; });
  useEffect(() => { load(); }, []);

  if (!exp || cont === undefined) return <div className="flex h-64 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;

  // Sin contratación todavía: mostrar el estado del expediente.
  if (!cont) {
    const pasos = [
      { k: "borrador", l: "Expediente en diligenciamiento" },
      { k: "enviado", l: "Enviado a HUMAN (confirmado por el empleado)" },
      { k: "validado", l: "Validado por HUMAN" },
    ];
    const orden = { borrador: 0, enviado: 1, validado: 2 };
    const actual = orden[exp.estado] ?? 0;
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div><h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mi contratación</h1><p className="mt-1 text-sm text-slate-500">Estado de tu proceso con HUMAN.</p></div>
          <ExpEstadoBadge estado={exp.estado} />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-5">
            {pasos.map((p, i) => (
              <div key={p.k} className="flex items-start gap-3">
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${i <= actual ? "bg-vino-700 text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
                <p className={`text-sm font-medium ${i <= actual ? "text-slate-800" : "text-slate-400"}`}>{p.l}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-lg border border-vino-100 bg-vino-50/60 p-4 text-sm text-vino-800">
            <FileSignature className="mt-0.5 h-5 w-5 shrink-0" />
            <p>Cuando HUMAN defina tu contratación y emita los documentos, aparecerán aquí para que los revises y firmes.</p>
          </div>
        </div>
      </div>
    );
  }

  const todos = cont.documentos || [];
  const docs = vigentes(cont);
  const anteriores = todos.filter((d) => d.estado === "reemplazado");
  const pendientes = porFirmar(cont);
  const gerencia = docs.some((d) => d.pendiente_gerencia);
  const abrirFirma = (d) => { setHashVisto(""); setLeido(false); setRevisar(d); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mi contratación</h1><p className="mt-1 text-sm text-slate-500">Contrato {cont.numero_contrato} · revisa y firma tus documentos.</p></div>
      </div>

      {pendientes.length > 0 && <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> Tienes {pendientes.length} documento(s) por firmar.</div>}
      {todoFirmado(cont) && <RogeFelicitacionCard gerencia={gerencia} />}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Documentos de tu contratación</h3>
        {docs.length === 0 ? <p className="text-sm text-slate-400">Aún no hay documentos emitidos.</p> : (
          <div className="divide-y divide-slate-100" data-testid="portal-docs">
            {docs.map((d) => <DocFila key={d.id} d={d} onVer={setViewDoc} onFirmar={abrirFirma} />)}
          </div>
        )}
        {anteriores.length > 0 && (
          <details className="mt-3 rounded-lg border border-slate-200 px-3" data-testid="portal-versiones-anteriores">
            <summary className="cursor-pointer py-2 text-xs font-semibold text-slate-500">Versiones anteriores ({anteriores.length}) — conservadas como evidencia</summary>
            <div className="divide-y divide-slate-100">{anteriores.map((d) => <DocFila key={d.id} d={d} onVer={setViewDoc} />)}</div>
          </details>
        )}
      </div>

      {revisar && (
        <DocViewer
          url={`/portal/contratacion/documentos/${revisar.id}/preview`}
          titulo={`Revisa antes de firmar: ${revisar.nombre}`}
          subtitulo="Este es exactamente el documento que quedará firmado y archivado"
          nombreArchivo={`${revisar.codigo || "documento"}.pdf`}
          onLoaded={(i) => setHashVisto(i.hash || "")}
          onFinLeido={() => setLeido(true)}
          onClose={() => setRevisar(null)}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-300" data-testid="visor-aviso-lectura">{leido ? "Llegaste al final del documento. Ya puedes firmarlo a mano." : "Desplázate hasta el final del documento para habilitar la firma."}</p>
            <button onClick={() => setFirmar(revisar)} disabled={!hashVisto || !leido} data-testid="visor-firmar-documento" className="flex items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-vino-800 hover:bg-vino-50 disabled:opacity-50"><PenLine className="h-4 w-4" /> Firmar documento</button>
          </div>
        </DocViewer>
      )}
      {firmar && <FirmaManuscritaModal doc={firmar} hashVisto={hashVisto} yaAvisado={!!cont?.alerta_sst} onClose={() => setFirmar(null)} onSigned={async () => { setFirmar(null); setRevisar(null); const c = await load(); if (todoFirmado(c)) setCelebrar(true); }} />}
      {celebrar && <RogeFelicitacionModal gerencia={gerencia} onClose={() => setCelebrar(false)} />}
      {viewDoc && (
        <DocViewer
          url={`/portal/contratacion/documentos/${viewDoc.id}/preview`}
          titulo={viewDoc.nombre}
          subtitulo={`${viewDoc.codigo || ""} · versión ${viewDoc.version || 1}${viewDoc.estado === "firmado" ? " · firmado (archivo oficial inalterable)" : viewDoc.estado === "reemplazado" ? " · versión anterior conservada como evidencia" : ""}`}
          nombreArchivo={`${viewDoc.codigo || "documento"}.pdf`}
          onClose={() => setViewDoc(null)}
        />
      )}
    </div>
  );
}
